import { KeyRound } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type { CustomerPortalPeople } from "@shared-core/data/customer-portal-access-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePortalPeople } from "@shared-core/domain/usePortalPeople";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";

/**
 * A customer's portal in one line on its Overview: how many people, how many
 * have signed in, whether staff have blocked it. Managing them is not done
 * here: it is the People tab of the portal's staff view, beside "view as"
 * (docs/features/customer-storefront/12_decision_access_for_every_customer.md),
 * which this links to.
 */
export function CustomerPortalSummary({ customerId }: { customerId: string }) {
  const capabilities = usePlatformCapabilities();
  const { state } = usePortalPeople(useDataAdapter(), customerId);

  const open = () =>
    capabilities.redirectTo(
      `/portal-view/customer/${encodeURIComponent(customerId)}/people`,
    );

  return (
    <section
      aria-label="Customer users"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4"
    >
      <div className="min-w-0 space-y-1">
        <h3 className="flex items-center gap-2 text-sm font-medium">
          <KeyRound className="size-4 text-muted-foreground" />
          Portal
          {state.status === "ready" && state.data.block && (
            <Badge variant="error">Blocked</Badge>
          )}
        </h3>
        {state.status === "loading" && (
          <p className="text-sm text-muted-foreground">
            Loading customer users…
          </p>
        )}
        {state.status === "error" && (
          <p className="text-sm text-muted-foreground">
            We could not load this customer&apos;s customer users.
          </p>
        )}
        {state.status === "ready" && <Counts data={state.data} />}
      </div>
      <Button variant="outline" size="sm" onClick={open}>
        Manage customer users
      </Button>
    </section>
  );
}

function Counts({ data }: { data: CustomerPortalPeople }) {
  const total = data.people.length;
  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No one linked yet. Everyone here can use the portal: people are added
        when they are picked as a contact or sign in.
      </p>
    );
  }
  const signedIn = data.people.filter((p) => p.has_signed_in).length;
  const wide = data.people.filter((p) =>
    p.extras.includes("customer_wide_view"),
  ).length;
  return (
    <p className="text-sm text-muted-foreground">
      {total} {total === 1 ? "person" : "people"}, {signedIn} signed in
      {wide > 0 ? `, ${wide} who can see all of the customer's work` : ""}.
      {data.block ? " Portal blocked." : ""}
    </p>
  );
}
